import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { fetchAdminRow, isEmailEnvAllowed, type AdminRow } from '../lib/admin';
import { fetchMemberships, type Memberships } from '../shared/memberships';
import { clearLegacySession } from '../shared/sessionMigration';
import { access as booksAccessApi } from '../lib/ledger/api';
import type { MyAccess } from '../lib/ledger/types';

type AuthState = {
  loading: boolean;
  user: User | null;
  session: Session | null;
  admin: AdminRow | null;
  envAllowed: boolean;
  /**
   * books_my_access() for the LLC books: view / edit / admin flags.
   * null until loaded, or when the RPC is missing (migration 278 not applied yet).
   */
  books: MyAccess | null;
  /** True when the user may use backbar: admin row, env allowlist, or books access. */
  authorized: boolean;
  /**
   * Books access with no admin row and no env seat (the outside accountant).
   * The nav shows Books + Help only and every other route goes to /books.
   */
  booksOnly: boolean;
  /** Every hat this account holds (app_my_memberships); null until loaded or on RPC failure. */
  memberships: Memberships | null;
};

type AuthCtx = AuthState & {
  signInWithPassword: (email: string, password: string) => Promise<{ error?: string }>;
  signInWithMagicLink: (email: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
};

const defaultState: AuthState = {
  loading: true,
  user: null,
  session: null,
  admin: null,
  envAllowed: false,
  books: null,
  authorized: false,
  booksOnly: false,
  memberships: null,
};

const Ctx = createContext<AuthCtx>({
  ...defaultState,
  signInWithPassword: async () => ({ error: 'not ready' }),
  signInWithMagicLink: async () => ({ error: 'not ready' }),
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(defaultState);

  useEffect(() => {
    let alive = true;

    async function resolve(session: Session | null) {
      if (!session?.user) {
        if (alive) setState({ ...defaultState, loading: false });
        return;
      }
      const email = session.user.email ?? null;
      const envAllowed = isEmailEnvAllowed(email);
      // Defer DB lookups out of onAuthStateChange to avoid blocking it.
      // Admin gate (unchanged) + memberships for the experience switcher
      // + books access for the LLC ledger. A failing books RPC never locks
      // an admin out; it only means the books tabs fall back to the admin rule.
      const [admin, memberships, books] = await Promise.all([
        fetchAdminRow(session.user.id),
        fetchMemberships(supabase).catch((err: unknown) => {
          console.warn('[backbar] fetchMemberships failed:', err);
          return null;
        }),
        booksAccessApi.mine().then((r) => r ?? null).catch((err: unknown) => {
          console.warn('[backbar] books_my_access failed (migration 278 not applied yet?):', err);
          return null;
        }),
      ]);
      if (!alive) return;
      const canViewBooks = Boolean(books?.can_view);
      setState({
        loading: false,
        user: session.user,
        session,
        admin,
        envAllowed,
        books,
        authorized: Boolean(admin) || envAllowed || canViewBooks,
        booksOnly: !admin && !envAllowed && canViewBooks,
        memberships,
      });
    }

    supabase.auth.getSession().then(({ data }) => {
      void resolve(data.session ?? null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      // resolve in a microtask so we never await inside the callback
      queueMicrotask(() => {
        void resolve(session);
      });
    });

    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // The main way in: the same email and password the person uses in the app
  // (Backbar shares the app's accounts). Who may actually use Backbar is still
  // decided after sign-in, by the allowlist, the admin_users row, or a
  // books_access row.
  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (error) {
      return {
        error:
          error.message === 'Invalid login credentials'
            ? 'That email and password do not match. Check them, or use the emailed link below.'
            : error.message,
      };
    }
    return {};
  };

  const signInWithMagicLink = async (email: string) => {
    const cleanEmail = email.trim().toLowerCase();
    // Gate the magic-link send to the env allowlist OR a known admin_users row.
    // We can't check admin_users from anon, so env list is the practical gate
    // pre-login; the post-login authorized check is the real enforcer.
    if (!isEmailEnvAllowed(cleanEmail)) {
      // Still send — admin_users or books_access may grant access — but we surface a hint.
      // (If DB blocks them, the post-login screen explains.)
    }
    const redirectTo = window.location.origin + '/';
    const { error } = await supabase.auth.signInWithOtp({
      email: cleanEmail,
      options: { emailRedirectTo: redirectTo },
    });
    if (error) return { error: error.message };
    return {};
  };

  const signOut = async () => {
    // Also drop the pre-cookie localStorage session so sign-out is complete
    // (the migration bridge deliberately leaves it in place otherwise).
    clearLegacySession();
    await supabase.auth.signOut();
    setState({ ...defaultState, loading: false });
  };

  return (
    <Ctx.Provider value={{ ...state, signInWithPassword, signInWithMagicLink, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  return useContext(Ctx);
}

/**
 * What the signed-in person may do in Books. The database decides for real
 * (books_can_view / books_can_edit); this mirrors its answer so the UI can
 * disable write controls up front.
 *
 * When books_my_access answered, it is the truth. When it is missing (the
 * ledger migration is not applied yet) the database still runs the older
 * admin-only rule, so the fallback mirrors that: admins and env seats view,
 * admins edit. Once the RPC exists, admins start as view-only like everyone.
 */
export function useBooksAccess(): { canView: boolean; canEdit: boolean; isAdmin: boolean; loading: boolean } {
  const { loading, admin, envAllowed, books } = useContext(Ctx);
  const isAdmin = Boolean(admin) || Boolean(books?.is_admin);
  const canView = books ? books.can_view || isAdmin : Boolean(admin) || envAllowed;
  const canEdit = books ? books.can_edit : Boolean(admin);
  return { canView, canEdit, isAdmin, loading };
}
